/**
 * 62.54 step 5 (ARCHITECTURE §5.6 "62.54, the design"): weather carried by the cells, behind `GameConfig.weatherCells`.
 * The wind field's cells (core/windField.ts cellsAt — the planet's keyed timeline as lows and highs drifting with the
 * bands) carry their kind's weather: the weather at a place is the cell that covers it most — rain under a low, a
 * storm under a storm cell, clear or haze under a high, clear where no cell reaches. A cell covers fully inside its
 * radius and fades to nothing at WEATHER_CELLS.edge radii, eased with its birth and fading; a **fog bank pools in low
 * ground**: its cover × how low the place lies on the planet's generated height range (the valley first, the ridge
 * last). The effect (sight, perception, hearing, wind share) blends from clear across a cell's edge — sight by its
 * reciprocal, so clear's unlimited sight blends to the cell's smoothly. Pure: dmath, keyed draws.
 */
import type { GameState, Vec3 } from '../types';
import { WEATHER_EFFECTS, type WeatherEffect, type WeatherKind } from './weather';
import { cellsAt, weatherTime } from './windField';
import { WEATHER_CELLS } from '../constants/action';
import { getSurfaceRadius } from './surface';
import { generatedRange } from './groundMaterial';
import { dacos } from './dmath';
import { vecDot, vecNormalize } from '../math';
import { datan2 } from './dmath';
import { spinAxisOf } from './spinAxis';

type CellState = Pick<GameState, 'config' | 'turnNumber' | 'planets'>;

/** smoothstep from a to b. */
function smooth(a: number, b: number, x: number): number { const t = Math.max(0, Math.min(1, (x - a) / (b - a))); return t * t * (3 - 2 * t); }

/** How low a place lies on its planet, 0 (the highest generated ground) … 1 (the lowest): a fog bank fills the low ground first. */
export function lowness(state: CellState, planetIndex: number, dir: Vec3): number {
  const planet = state.planets[planetIndex];
  if (!planet) return 0;
  const initial = planet.initialHeightmap ?? planet.heightmap;
  const { min, range } = generatedRange(initial);
  const h = (getSurfaceRadius(planet, dir) - planet.radius - min) / range;   // 0 lowest … 1 highest generated ground
  return 1 - smooth(WEATHER_CELLS.fogPool[0], WEATHER_CELLS.fogPool[1], h);
}

/** The cell weather at a place: the kind of the cell that covers it most, and how much (0–1). Clear and 0 where none reaches. */
export function cellWeather(state: CellState, planetIndex: number, dir: Vec3, seconds = 0): { kind: WeatherKind; cover: number } {
  const n = vecNormalize(dir);
  let best: WeatherKind = 'clear', cover = 0;
  let low = -1;   // the place's lowness, found once if a fog bank asks
  for (const c of cellsAt(state, planetIndex, weatherTime(state, seconds))) {
    const cosR = Math.max(-1, Math.min(1, vecDot(c.centre, n)));
    if (cosR < c.cosReach) continue;   // beyond 4 radii, far past its edge
    const r = dacos(cosR);
    let k = c.ease * (1 - smooth(c.radius, WEATHER_CELLS.edge * c.radius, r));
    if (k <= 0) continue;
    if (c.kind === 'fog') { if (low < 0) low = lowness(state, planetIndex, n); k *= low; }
    if (k > cover) { cover = k; best = c.kind; }
  }
  return { kind: best, cover };
}

/** The weather's effect at a place under the cells: clear blended toward the covering cell's kind by its cover. */
export function cellWeatherEffect(state: CellState, planetIndex: number, dir: Vec3, seconds = 0): WeatherEffect {
  const { kind, cover } = cellWeather(state, planetIndex, dir, seconds);
  const clear = WEATHER_EFFECTS.clear, w = WEATHER_EFFECTS[kind];
  if (cover <= 0 || kind === 'clear') return clear;
  if (cover >= 1) return w;
  const inv = cover / w.sight;   // clear's sight is unlimited (1/∞ = 0): blend the reciprocal
  return {
    sight: inv > 0 ? 1 / inv : Infinity,
    perception: clear.perception + (w.perception - clear.perception) * cover,
    hearing: clear.hearing + (w.hearing - clear.hearing) * cover,
    wind: clear.wind + (w.wind - clear.wind) * cover,
  };
}

/** 74.11 (a): a front due over a place — its kind, the seconds until its cover reaches a half, its compass bearing (degrees clockwise from the planet's north, the spin axis). */
export interface FrontArrival { kind: WeatherKind; seconds: number; bearing: number; /** the covering cell's centre (a direction) — the maps draw the arrow in their own frame; null for a clearing */ centre: Vec3 | null }
export const ARRIVALS = { horizonS: 1200, stepS: 30, count: 3 };
/**
 * The fronts due over `dir` from `seconds` on (the cells on; none with a fixed window): the place's weather sampled
 * ahead every `stepS` to `horizonS`, each change of kind to a weather an arrival with its bearing from the covering
 * cell's centre. One source for the minimap's line, the HUD's plate and the installations (74.11 (b) reads it). Pure.
 */
export function frontArrivals(state: CellState, planetIndex: number, dir: Vec3, seconds = 0, count = ARRIVALS.count): FrontArrival[] {
  if (!state.config.weather || !state.config.weatherCells || state.config.weatherFixed) return [];
  const planet = state.planets[planetIndex];
  if (!planet) return [];
  const n = vecNormalize(dir), axis = spinAxisOf(planet);
  const north = vecNormalize({ x: axis.x - n.x * vecDot(axis, n), y: axis.y - n.y * vecDot(axis, n), z: axis.z - n.z * vecDot(axis, n) });
  const east = { x: north.y * n.z - north.z * n.y, y: north.z * n.x - north.x * n.z, z: north.x * n.y - north.y * n.x };
  const out: FrontArrival[] = [];
  let last = cellWeather(state, planetIndex, n, seconds).kind;
  for (let t = ARRIVALS.stepS; t <= ARRIVALS.horizonS && out.length < count; t += ARRIVALS.stepS) {
    const at = seconds + t;
    const w = cellWeather(state, planetIndex, n, at);
    const kind = w.cover >= 0.5 ? w.kind : 'clear';
    if (kind === last) continue;
    last = kind;
    if (kind === 'clear') { out.push({ kind, seconds: t, bearing: 0, centre: null }); continue; }   // (74.11 (b): a clearing is an arrival too — "clearing in 11 min")
    let bearing = 0, centre: Vec3 | null = null;
    for (const c of cellsAt(state, planetIndex, weatherTime(state, at))) if (c.kind === kind) {   // the covering cell: its centre's bearing from here
      const cosA = vecDot(c.centre, n), tx = c.centre.x - n.x * cosA, ty = c.centre.y - n.y * cosA, tz = c.centre.z - n.z * cosA;
      const deg = datan2(tx * east.x + ty * east.y + tz * east.z, tx * north.x + ty * north.y + tz * north.z) * 180 / Math.PI;
      bearing = (deg + 360) % 360; centre = c.centre; break;
    }
    out.push({ kind, seconds: t, bearing, centre });
  }
  return out;
}
